import React, { useState, useEffect } from "react";
import { X, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import { Button, Spinner } from "./ui/kit";
import { fmtKc, fmtPct } from "../lib/format";
import api, { apiError } from "../lib/api";

const STRATEGIE = [
  { key: "DRZET_MARZI", label: "Držet marži", hint: "Marže % zůstává, prodejní cena roste s nákupní." },
  { key: "DRZET_CENU", label: "Držet cenu", hint: "Prodejní cena zůstává, marže se dopočítá." },
];

function ZmenaStitek({ pct }) {
  if (!pct) return <span className="text-[11px] text-muted">beze změny</span>;
  const up = pct > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-700 ${up ? "text-danger" : "text-success"}`}>
      {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{up ? "▲" : "▼"} {Math.abs(pct)} %
    </span>
  );
}

export default function PrepocetPanel({ nid, onClose, onCreated }) {
  const [strategie, setStrategie] = useState("DRZET_MARZI");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setBusy(true); setErr("");
    api.post(`/api/nabidky/${nid}/prepocet/nahled`, { strategie })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(apiError(e.response?.data?.detail)); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [nid, strategie]);

  const createV2 = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/api/nabidky/${nid}/verze`, { strategie }); onCreated?.(); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  const s = data?.souhrn;

  return (
    <div className="fixed inset-0 z-[70] flex md:items-center items-end justify-center md:p-4" data-testid="prepocet-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[820px] md:rounded-card rounded-t-sheet shadow-panel max-h-[90vh] flex flex-col">
        <div className="border-b border-line-softer px-5 py-4 flex items-center justify-between">
          <h2 className="text-[15px] font-800">Přepočet na dnešní ceník</h2>
          <button onClick={onClose} className="text-muted hover:text-ink" data-testid="prepocet-close"><X size={20} /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {err && <div className="rounded-input bg-[#fdecea] text-danger text-[12.5px] px-3 py-2" data-testid="prepocet-error">{err}</div>}

          <div className="flex gap-2">
            {STRATEGIE.map((st) => (
              <button key={st.key} onClick={() => setStrategie(st.key)} data-testid={`strategie-${st.key}`}
                className={`flex-1 rounded-card border px-4 py-3 text-left transition-colors ${strategie === st.key ? "border-accent bg-inputbg" : "border-line-strong hover:border-accent"}`}>
                <div className="text-[13px] font-700 text-ink">{st.label}</div>
                <div className="text-[11.5px] text-muted mt-0.5">{st.hint}</div>
              </button>
            ))}
          </div>

          {busy && !data && <div className="flex justify-center py-6"><Spinner /></div>}

          {data && (
            <div className="border border-line-softer rounded-card overflow-hidden">
              <div className="grid grid-cols-[1fr_120px_120px_92px] gap-2 px-3 py-1.5 label-up text-muted border-b border-line-softer">
                <span>Položka</span><span className="text-right">Nákup</span><span className="text-right">Prodej / marže</span><span className="text-right">Změna</span>
              </div>
              <div className="max-h-[340px] overflow-y-auto">
                {data.radky.map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_120px_120px_92px] gap-2 items-center px-3 py-2 border-t border-line-softer text-[12.5px]" data-testid={`diff-row-${i}`}>
                    <span className="min-w-0 truncate">{r.nazev}<span className="text-muted"> · {r.mnozstvi}×</span></span>
                    <span className="text-right amount">
                      {r.beze_zmeny ? <span className="text-muted">{fmtKc(r.nova_nakup)}</span> : <>
                        <span className="text-muted line-through mr-1 text-[11px]">{fmtKc(r.stara_nakup)}</span>
                        <span className="text-ink font-600">{fmtKc(r.nova_nakup)}</span></>}
                    </span>
                    <span className="text-right amount">
                      {fmtKc(r.nova_prodej)}
                      <span className={`ml-1 text-[10.5px] ${r.pod_nakupni ? "text-danger font-700" : "text-muted"}`}>{fmtPct(r.nova_marze)} %</span>
                      {r.pod_nakupni && <AlertTriangle size={11} className="inline ml-0.5 text-danger" title="Pod nákupní cenou" data-testid={`pod-nakupni-${i}`} />}
                    </span>
                    <span className="text-right"><ZmenaStitek pct={r.zmena_pct} /></span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {s && (
            <div className="rounded-card bg-sidebar p-4 flex items-center justify-between" data-testid="prepocet-souhrn">
              <div className="text-[12.5px] text-ondark-muted">
                Základ <span className="amount line-through mr-1">{fmtKc(s.stary_zaklad)}</span>
                <span className="amount text-ondark font-700">{fmtKc(s.novy_zaklad)} Kč</span>
              </div>
              <div className={`amount text-[15px] font-800 ${s.rozdil_kc > 0 ? "text-[#ff7a7a]" : s.rozdil_kc < 0 ? "text-[#7ee0a1]" : "text-ondark-muted"}`} data-testid="prepocet-rozdil">
                {s.rozdil_kc > 0 ? "+" : ""}{fmtKc(s.rozdil_kc)} Kč ({s.rozdil_pct > 0 ? "+" : ""}{s.rozdil_pct} %)
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-line-softer px-5 py-3 flex justify-between">
          <button onClick={onClose} className="btn-ghost" data-testid="prepocet-zrusit">Zrušit</button>
          <Button onClick={createV2} disabled={busy || !data} data-testid="vytvorit-v2">{busy ? <Spinner /> : "Vytvořit novou verzi"}</Button>
        </div>
      </div>
    </div>
  );
}
